import { useEffect, useRef, useCallback, useState } from "react";
import { AnyTrackLocation } from "../../../../api/anyTrackApi";

interface UseAnyTrackMarkersProps {
  map: google.maps.Map | null;
  locations: AnyTrackLocation[];
  isVisible: boolean;
  searchQuery?: string;
  selectedTrailerId?: string;
  onTrailerClick?: (deviceId: string) => void;
}

const TRAILER_COLOR = "#facc15";
const TRAILER_SELECTED_COLOR = "#f59e0b";
const TRAILER_UNPLUGGED_COLOR = "#f44336";
const TRAILER_TEXT_COLOR = "#1a1a1a";
const TRAILER_UNPLUGGED_TEXT_COLOR = "#ffffff";

export const createTrailerMarkerElement = (
  location: Pick<AnyTrackLocation, "deviceId" | "vehicleName" | "deviceStatus">,
  isSelected = false,
): HTMLElement => {
  const label = location.vehicleName || location.deviceId.slice(-8);
  const isUnplugged = location.deviceStatus === "Unplugged";
  const bgColor = isUnplugged
    ? TRAILER_UNPLUGGED_COLOR
    : isSelected
      ? TRAILER_SELECTED_COLOR
      : TRAILER_COLOR;
  const textColor = isUnplugged ? TRAILER_UNPLUGGED_TEXT_COLOR : TRAILER_TEXT_COLOR;
  const borderStyle = isSelected ? "border: 2px solid #fff; box-shadow: 0 0 0 2px #f59e0b, 0 2px 6px rgba(0,0,0,0.4);" : isUnplugged ? "border: 2px solid #d32f2f; box-shadow: 0 0 8px rgba(244,67,54,0.5);" : "box-shadow: 0 2px 4px rgba(0,0,0,0.3);";
  const scale = isSelected ? "transform: scale(1.15);" : "";

  const el = document.createElement("div");
  el.style.cssText = `display:flex;flex-direction:column;align-items:center;cursor:pointer;${scale}`;

  el.innerHTML = `
    <div style="
      background-color: ${bgColor};
      color: ${textColor};
      border-radius: 5px;
      padding: 3px 7px;
      font-size: 11px;
      font-weight: 700;
      white-space: nowrap;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      ${borderStyle}
      font-family: sans-serif;
      letter-spacing: 0.3px;
    ">${label}</div>
    <div style="
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 6px solid ${bgColor};
    "></div>
  `;

  return el;
};

export const useAnyTrackMarkers = ({
  map,
  locations,
  isVisible,
  searchQuery = "",
  selectedTrailerId,
  onTrailerClick,
}: UseAnyTrackMarkersProps) => {
  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const markerLibraryRef = useRef<typeof google.maps.marker.AdvancedMarkerElement | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    if (map) setIsMapReady(true);
  }, [map]);

  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker"
      )) as google.maps.MarkerLibrary;
      markerLibraryRef.current = AdvancedMarkerElement;
    }
    return markerLibraryRef.current;
  }, []);

  const clearAllMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!map || !isMapReady) return;

    if (!isVisible) {
      clearAllMarkers();
      return;
    }

    let cancelled = false;

    const updateMarkers = async () => {
      const AdvancedMarkerElement = await loadMarkerLibrary();
      if (cancelled || !AdvancedMarkerElement) return;

      const normalizedQuery = searchQuery.trim().toLowerCase();

      // Removed devices never appear on the map
      const activeLocations = locations.filter((l) => !l.isRemoved);

      const filteredLocations = normalizedQuery
        ? activeLocations.filter(
            (l) =>
              l.vehicleName?.toLowerCase().includes(normalizedQuery) ||
              l.licensePlateNumber?.toLowerCase().includes(normalizedQuery) ||
              l.deviceId.toLowerCase().includes(normalizedQuery)
          )
        : activeLocations;

      const visibleIds = new Set(filteredLocations.map((l) => l.deviceId));

      // Hide markers not matching the search
      for (const [id, marker] of markersRef.current.entries()) {
        if (!visibleIds.has(id)) {
          marker.map = null;
        }
      }

      for (const location of filteredLocations) {
        if (!location.latitude || !location.longitude) continue;

        const position = { lat: location.latitude, lng: location.longitude };
        const isSelected = location.deviceId === selectedTrailerId;
        const markerElement = createTrailerMarkerElement(location, isSelected);
        const existingMarker = markersRef.current.get(location.deviceId);

        if (existingMarker) {
          existingMarker.position = position;
          existingMarker.content = markerElement;
          existingMarker.map = map;
        } else {
          const marker = new AdvancedMarkerElement({
            position,
            content: markerElement,
            map,
            title: location.vehicleName,
          });
          if (onTrailerClick) {
            marker.addListener("click", () => onTrailerClick(location.deviceId));
          }
          markersRef.current.set(location.deviceId, marker);
        }
      }
    };

    updateMarkers();

    return () => {
      cancelled = true;
    };
  }, [map, locations, isVisible, searchQuery, isMapReady, loadMarkerLibrary, clearAllMarkers, selectedTrailerId, onTrailerClick]);

  useEffect(() => {
    return () => {
      clearAllMarkers();
    };
  }, [clearAllMarkers]);
};
